import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DEFAULT_TAB } from "../constants";
import useAppPersistence from "./persistence/useAppPersistence";

/**
 * Hooks for managing tab navigation with URL query parameters
 * @param {String} useTabPersistence - Whether or not to use persistence or local state
 */
const useWidgetTabNavigation = (useTabPersistence = true) => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const currentParams = useMemo(() => new URLSearchParams(search), [search]);
  const page = pathname?.split("/")?.[1];
  const resourceId = pathname?.split("/")?.[2];

  const {
    getPageSettings,
    getActiveTab,
    setActiveTab: setActiveTabPersistence,
    getCurrentResourceScreen,
  } = useAppPersistence();

  const pageSettings = getPageSettings(page);

  const persistedActiveTab = useMemo(
    () => getActiveTab({ page, resourceId }),
    [page, resourceId, getActiveTab]
  );

  const activeTab = persistedActiveTab || DEFAULT_TAB(page);

  const [localActiveTab, setLocalActiveTab] = useState(activeTab);

  const setActiveTab = useCallback(
    async (tab) => {
      currentParams.set("tab", tab); // update or add the tab param

      const newSearch = `?${currentParams.toString()}`;
      const newPathname = `${pathname}${newSearch}`;

      setActiveTabPersistence({ page, tab, url: newPathname, resourceId });
      setLocalActiveTab(tab);

      history.push(newSearch);
    },
    [
      currentParams,
      pathname,
      history,
      page,
      setActiveTabPersistence,
      resourceId,
    ]
  );

  // Used for initial persistence on initial detail tab navigation
  useEffect(() => {
    const currentResourceScreen = getCurrentResourceScreen(page);
    const lastItem = currentResourceScreen?.split("/")[2];
    if (!lastItem) {
      currentParams.set("tab", activeTab);

      const newSearch = `?${currentParams.toString()}`;
      const newPathname = `${pathname}${newSearch}`;
      setActiveTabPersistence({
        page,
        tab: activeTab,
        url: newPathname,
        resourceId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    currentParams,
    pathname,
    page,
    resourceId,
    persistedActiveTab,
  ]);

  // This useEffect handles copy/pasting a URL
  const params = useMemo(() => new URLSearchParams(search), [search]);
  useEffect(() => {
    if (
      localActiveTab !== params.get("tab") &&
      pageSettings &&
      useTabPersistence
    ) {
      currentParams.set("tab", params.get("tab"));

      const newSearch = `?${currentParams.toString()}`;
      const newPathname = `${pathname}${newSearch}`;
      setActiveTabPersistence({
        page,
        tab: params.get("tab"),
        url: newPathname,
        resourceId,
      });
      setLocalActiveTab(params.get("tab"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    useTabPersistence,
    pageSettings,
    pathname,
    page,
    resourceId,
    params,
    localActiveTab,
  ]);

  return {
    activeTab: localActiveTab,
    setActiveTab,
    localActiveTab,
    setLocalActiveTab,
  };
};

export default useWidgetTabNavigation;
